import React, { FunctionComponent } from 'react';
import { Table } from 'semantic-ui-react';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { map, pathOr, addIndex } from 'ramda';

import { ClassProp } from 'helpers/types';

import styles from './FilterableTable.styles';

export type TableLayout = {
  source?: string[];
  SourceComponent?: Function;
  sourceComponentOptions?: Record<string, unknown>;
  label?: string;
  LabelComponent?: Function;
  labelComponentOptions?: Record<string, unknown>;
};

type Props = {
  tableLayout: TableLayout[];
  data: Record<string, unknown>[];
  classes: ClassProp<typeof styles>;
  className?: string;
};

const mapIndexed = addIndex<any>(map);

const renderHeaderCell = (classes: ClassProp<typeof styles>) => (
  { label, LabelComponent, labelComponentOptions = {} }: TableLayout,
  idx: number,
) => (
  <Table.HeaderCell className={classes.tableHeader} key={idx}>
    {LabelComponent ? <LabelComponent {...labelComponentOptions} /> : label}
  </Table.HeaderCell>
);

const renderTableHeader = (
  classes: ClassProp<typeof styles>,
  tableLayout: TableLayout[],
) => (
  <Table.Header>
    <Table.Row>{mapIndexed(renderHeaderCell(classes), tableLayout)}</Table.Row>
  </Table.Header>
);

const renderBodyCell = (record: Record<string, unknown>) => (
  { SourceComponent, source = [] }: TableLayout,
  idx: number,
) => {
  if (SourceComponent) {
    return (
      <Table.Cell key={idx}>
        <SourceComponent row={record} />
      </Table.Cell>
    );
  }
  let fieldValue = pathOr('', source, record);
  if (Array.isArray(fieldValue)) {
    fieldValue = fieldValue.join(', ');
  }

  return <Table.Cell key={idx}>{fieldValue}</Table.Cell>;
};

const renderTableRow = (tableLayout: TableLayout[]) => (
  data: Record<string, unknown>,
  idx: number,
) => (
  <Table.Row key={idx}>
    {mapIndexed(renderBodyCell(data), tableLayout)}
  </Table.Row>
);

const FilterableTableComponent: FunctionComponent<Props> = ({
  tableLayout,
  data,
  classes,
  className,
}) => {
  return (
    <Table className={classnames(className, classes.container)} selectable>
      {renderTableHeader(classes, tableLayout)}
      <Table.Body>{mapIndexed(renderTableRow(tableLayout), data)}</Table.Body>
    </Table>
  );
};

export default withStyles(styles)(FilterableTableComponent);
